import { MakeLogicType, afterMount, connect, kea, listeners, path, selectors } from 'kea'
import { loaders } from 'kea-loaders'
import { subscriptions } from 'kea-subscriptions'

import api from 'lib/api'
import { TaxonomicFilterGroupType } from 'lib/components/TaxonomicFilter/types'
import { GroupsAccessStatus, groupsAccessLogic } from 'lib/introductions/groupsAccessLogic'
import { wordPluralize } from 'lib/utils/strings'
import { projectLogic } from 'scenes/projectLogic'

import { GroupType, GroupTypeIndex } from '~/types'

export interface Noun {
    singular: string
    plural: string
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface groupsModelValues {
    groupsAccessStatus: GroupsAccessStatus // groupsAccessLogic
    groupsEnabled: boolean // groupsAccessLogic
    currentProjectId: number | null // projectLogic
    aggregationLabel: (groupTypeIndex: number | null | undefined, deferToUserWording?: boolean) => Noun
    groupNamesTaxonomicTypes: TaxonomicFilterGroupType[]
    groupTypes: Map<GroupTypeIndex, GroupType>
    groupTypesLoading: boolean
    groupTypesRaw: Array<GroupType>
    groupTypesRawLoading: boolean
    groupsTaxonomicTypes: TaxonomicFilterGroupType[]
    showGroupsOptions: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface groupsModelActions {
    createDetailDashboard: (groupTypeIndex: number) => number
    createDetailDashboardFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    createDetailDashboardSuccess: (
        groupTypesRaw: any[],
        payload?: number
    ) => {
        groupTypesRaw: any[]
        payload?: number
    }
    deleteGroupType: (groupTypeIndex: number) => number
    deleteGroupTypeFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    deleteGroupTypeSuccess: (
        groupTypesRaw: never[],
        payload?: number
    ) => {
        groupTypesRaw: never[]
        payload?: number
    }
    loadAllGroupTypes: () => any
    loadAllGroupTypesFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadAllGroupTypesSuccess: (
        groupTypesRaw: GroupType[],
        payload?: any
    ) => {
        groupTypesRaw: GroupType[]
        payload?: any
    }
    removeDetailDashboard: (dashboardId: number) => number
    removeDetailDashboardFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    removeDetailDashboardSuccess: (
        groupTypesRaw: GroupType[],
        payload?: number
    ) => {
        groupTypesRaw: GroupType[]
        payload?: number
    }
    setDefaultColumns: ({ groupTypeIndex, defaultColumns }: { defaultColumns: string[]; groupTypeIndex: number }) => {
        groupTypeIndex: number
        defaultColumns: string[]
    }
    setDefaultColumnsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    setDefaultColumnsSuccess: (
        groupTypesRaw: any[],
        payload?: {
            groupTypeIndex: number
            defaultColumns: string[]
        }
    ) => {
        groupTypesRaw: any[]
        payload?: {
            groupTypeIndex: number
            defaultColumns: string[]
        }
    }
    updateGroupTypesMetadata: (payload: Array<GroupType>) => Array<GroupType>
    updateGroupTypesMetadataFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateGroupTypesMetadataSuccess: (
        groupTypesRaw: GroupType[],
        payload?: Array<GroupType>
    ) => {
        groupTypesRaw: GroupType[]
        payload?: Array<GroupType>
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface groupsModelMeta {
    __keaTypeGenInternalSelectorTypes: {
        groupTypes: (groupTypesRaw: GroupType[]) => Map<GroupTypeIndex, GroupType>
        groupTypesLoading: (groupTypesRawLoading: boolean) => boolean
        showGroupsOptions: (
            groupsAccessStatus: GroupsAccessStatus,
            groupsEnabled: boolean,
            groupTypes: Map<GroupTypeIndex, GroupType>
        ) => boolean
        groupsTaxonomicTypes: (groupTypes: Map<GroupTypeIndex, GroupType>) => TaxonomicFilterGroupType[]
        groupNamesTaxonomicTypes: (groupTypes: Map<GroupTypeIndex, GroupType>) => TaxonomicFilterGroupType[]
        aggregationLabel: (
            groupTypes: Map<GroupTypeIndex, GroupType>
        ) => (groupTypeIndex: number | null | undefined, deferToUserWording?: boolean) => Noun
    }
}

export type groupsModelType = MakeLogicType<groupsModelValues, groupsModelActions, Record<string, any>, groupsModelMeta>

export const groupsModel = kea<groupsModelType>([
    path(['models', 'groupsModel']),
    connect(() => ({
        values: [projectLogic, ['currentProjectId'], groupsAccessLogic, ['groupsEnabled', 'groupsAccessStatus']],
    })),
    loaders(({ values }) => ({
        groupTypesRaw: [
            [] as Array<GroupType>,
            {
                loadAllGroupTypes: async () => {
                    if (!values.currentProjectId) {
                        return []
                    }
                    return await api.get(`api/projects/${values.currentProjectId}/groups_types`)
                },
                updateGroupTypesMetadata: async (payload: Array<GroupType>) => {
                    if (values.groupsEnabled) {
                        return await api.update(
                            `/api/projects/${values.currentProjectId}/groups_types/update_metadata`,
                            payload
                        )
                    }
                    return []
                },
                deleteGroupType: async (groupTypeIndex: number) => {
                    if (values.groupsEnabled) {
                        await api.delete(`/api/projects/${values.currentProjectId}/groups_types/${groupTypeIndex}`)
                    }
                    return []
                },
                createDetailDashboard: async (groupTypeIndex: number) => {
                    const groupType = await api.put(
                        `/api/projects/${values.currentProjectId}/groups_types/create_detail_dashboard`,
                        { group_type_index: groupTypeIndex }
                    )
                    return values.groupTypesRaw.map((gt) => (gt.group_type_index === groupTypeIndex ? groupType : gt))
                },
                removeDetailDashboard: async (dashboardId: number) => {
                    return values.groupTypesRaw.map((gt) => {
                        if (gt.detail_dashboard === dashboardId) {
                            return {
                                ...gt,
                                detail_dashboard: null,
                            }
                        }
                        return gt
                    })
                },
                setDefaultColumns: async ({
                    groupTypeIndex,
                    defaultColumns,
                }: {
                    groupTypeIndex: number
                    defaultColumns: string[]
                }) => {
                    const groupType = await api.put(
                        `/api/projects/${values.currentProjectId}/groups_types/set_default_columns`,
                        { group_type_index: groupTypeIndex, default_columns: defaultColumns }
                    )
                    return values.groupTypesRaw.map((gt) => (gt.group_type_index === groupTypeIndex ? groupType : gt))
                },
            },
        ],
    })),
    selectors({
        groupTypes: [
            (s) => [s.groupTypesRaw],
            (groupTypesRaw: Array<GroupType>) =>
                new Map<GroupTypeIndex, GroupType>(
                    (groupTypesRaw ?? []).map((groupType) => [groupType.group_type_index, groupType])
                ),
        ],
        groupTypesLoading: [(s) => [s.groupTypesRawLoading], (groupTypesRawLoading: boolean) => groupTypesRawLoading],

        showGroupsOptions: [
            (s) => [s.groupsAccessStatus, s.groupsEnabled, s.groupTypes],
            (status: GroupsAccessStatus, enabled: boolean, groupTypes: Map<GroupTypeIndex, GroupType>) =>
                status !== GroupsAccessStatus.Hidden || (enabled && Array.from(groupTypes.values()).length > 0),
        ],
        groupsTaxonomicTypes: [
            (s) => [s.groupTypes],
            (groupTypes: Map<GroupTypeIndex, GroupType>): TaxonomicFilterGroupType[] => {
                return Array.from(groupTypes.values()).map(
                    (groupType: GroupType) =>
                        `${TaxonomicFilterGroupType.GroupsPrefix}_${groupType.group_type_index}` as TaxonomicFilterGroupType
                )
            },
        ],
        groupNamesTaxonomicTypes: [
            (s) => [s.groupTypes],
            (groupTypes: Map<GroupTypeIndex, GroupType>): TaxonomicFilterGroupType[] => {
                return Array.from(groupTypes.values()).map(
                    (groupType: GroupType) =>
                        `${TaxonomicFilterGroupType.GroupNamesPrefix}_${groupType.group_type_index}` as TaxonomicFilterGroupType
                )
            },
        ],
        aggregationLabel: [
            (s) => [s.groupTypes],
            (groupTypes: Map<GroupTypeIndex, GroupType>) =>
                (groupTypeIndex: number | null | undefined, deferToUserWording: boolean = false): Noun => {
                    if (groupTypeIndex != undefined) {
                        const groupType = groupTypes.get(groupTypeIndex as GroupTypeIndex)
                        if (groupType) {
                            return {
                                singular: groupType.name_singular || groupType.group_type || 'group',
                                plural: groupType.name_plural || wordPluralize(groupType.group_type) || 'groups',
                            }
                        }
                        return {
                            singular: 'unknown group',
                            plural: 'unknown groups',
                        }
                    }
                    return deferToUserWording
                        ? {
                              singular: 'user',
                              plural: 'users',
                          }
                        : { singular: 'person', plural: 'persons' }
                },
        ],
    }),
    subscriptions(({ values }) => ({
        groupsEnabled: (enabled) => {
            // Load the groups types in the case of groups becoming an available feature after this logic is mounted
            if (!values.groupTypesLoading && enabled) {
                groupsModel.actions.loadAllGroupTypes()
            }
        },
    })),
    listeners(({ actions }) => ({
        deleteGroupTypeSuccess: () => {
            actions.loadAllGroupTypes()
        },
    })),
    afterMount(({ actions }) => {
        if (window.POSTHOG_APP_CONTEXT?.current_team?.group_types) {
            actions.loadAllGroupTypesSuccess(window.POSTHOG_APP_CONTEXT.current_team.group_types)
        } else {
            actions.loadAllGroupTypes()
        }
    }),
])
